import { useCallback } from 'react'
import { useOrgParams } from '~/common/use-org-params';
import { supabaseGetter } from "~/api/supabase-getter"
import { useUpdateCrop, useCreateTasksBulk } from "~/api/supabase-mutations"
import { useCropsContextDispatch, cropEdited } from '~/common/context/crops/crops-context';
import _ from 'lodash';
import { useCurrentUser } from '~/common/use-current-user';
import { supabase } from '~/supabase-client';

export const useUpdateExistingCrop = ({
  species,
  variety,
  archetype,
  count,
  growthType,
  lifecycleType,
  notes,
  orgId = null,
}) => {
  const { farmId, plotId, patchId } = useOrgParams()
  const { currentUser } = useCurrentUser()
  const cropsDispatch = useCropsContextDispatch()
  const { updateCrop } = useUpdateCrop()
  const { createTasksBulk } = useCreateTasksBulk()

  const updateExistingCrop = useCallback(async ({ cropId, tasks = null }) => {
    // org pointer hierarchy
    const orgIdHierarchy = orgId || patchId || plotId || farmId

    if (!species) return

    const { error: cropError } = await updateCrop(
      cropId,
      {
        organization_id: orgIdHierarchy,
        template_id: variety?.id ?? species.id,
        created_by: currentUser.id,
        family_code: species.family_code,
        archetype_code: archetype.code,
        species_name: species.name,
        variety_name: variety?.name ?? '',
        name: variety ? `${species.name} - ${variety.name}` : species.name,
        count,
        notes,
        growth_code: growthType,
        lifecycle_code: lifecycleType,
      },
    )
    if (cropError) {
      console.error('Failed editing crop record:', cropError)
      throw cropError
    }

    // Update tasks if adjusted tasks provided
    if (tasks && tasks.length > 0) {
      // Delete existing tasks for this crop
      const { error: deleteError } = await supabase
        .from('tasks')
        .delete()
        .eq('crop_id', cropId)

      if (deleteError) {
        console.error('Failed deleting old task records:', deleteError)
        throw deleteError
      }

      // Insert new adjusted tasks using mutation hook
      // Filter out UI-specific fields (label, type, start, len) that exist for timeline rendering
      const tasksWithIds = tasks.map(task => ({
        // Only include valid database columns (omit 'id' — let DB generate it on re-insert)
        ..._.pick(task, [
          'task_code',
          'task_window_start',
          'duration',
          'state',
          'notes',
          'completed_by',
          'completed_at'
        ]),
        crop_id: cropId,
        organization_id: orgIdHierarchy,
        created_by: currentUser.id,
      }))

      const { error: tasksError } = await createTasksBulk(tasksWithIds)
      if (tasksError) {
        console.error('Failed creating adjusted task records:', tasksError)
        throw tasksError
      }
    }

    // Invalidate all caches
    supabaseGetter.crops.invalidate()
    supabaseGetter.cropSchedules.invalidate()
    supabaseGetter.cropPhases.invalidate()
    supabaseGetter.cropDetails.invalidate()
    supabaseGetter.tasks.invalidate()

    // Dispatch immediately to show optimistic UI
    cropsDispatch(cropEdited({ cropId }))

    // Retry fetch after delay to handle read-after-write consistency
    // Supabase replicas can take 100-500ms to sync
    setTimeout(() => {
      supabaseGetter.crops.invalidate()
      cropsDispatch(cropEdited({ cropId }))
    }, 500)
  }, [
    currentUser,
    archetype,
    count,
    updateCrop,
    createTasksBulk,
    cropsDispatch,
    species,
    farmId,
    growthType,
    lifecycleType,
    notes,
    patchId,
    plotId,
    variety,
    orgId,
  ])

  return { updateExistingCrop }
}